import { useState, useMemo } from 'react'
import { DndContext, useSensors, useSensor, PointerSensor } from '@dnd-kit/core'
import AuthService from 'services/auth'
import cutileiApi from 'services/cutileiApi'
import { getApiErrorMessage, logApiError } from 'services/apiError'
import TimeWindow from 'services/timeWindow'
import ScheduleGridService from 'services/ScheduleGrid'
import CreateScheduleModal from 'pages/Modals/CreateScheduleModal'
import ScheduleDetailsModal from 'pages/Modals/ScheduleDetailsModal'
import EditReservationModal from 'pages/Modals/EditReservationModal'
import EditBillModal from 'pages/Modals/EditBillModal'
import OptionDialog from 'components/OptionDialog'
import AlertDialog from 'components/AlertDialog'
import Schedule from 'components/Schedule'
import Professional from '../Professional'
import TimeBlock from '../TimeBlock'
import { DateTime } from 'luxon'

import {
  Container,
  ScheduleGrid,
  GridHeader,
  TimeMark,
  GridColumn,
  HorizontalLine,
  SubTitle,
  InfoText,
  LinkText,
  ProfessionalColumn
} from '../styles'

export default function WeekGrid ({
  professional,
  schedules = [],
  scheduleStatuses = [],
  workingDays = [],
  timeWindows = [],
  timeInterval,
  date,
  numColumns = 7,
  query,
  onScheduleCreate: handleScheduleCreated = () => {},
  onEditSchedule: handleEditSchedule = () => {},
  onEditReservation: handleEditReservation = () => {},
  onDeleteSchedule: handleDeleteSchedule = () => {},
  onDeleteAllSchedules: handleDeleteAllSchedules = () => {},
  onStatusListUpdated: handleStatusListUpdated = () => {},
  onEditScheduleStatus: handleEditSchedulesStatus = () => {},
  onSplitSchedule: handleSplitSchedule = () => {},
  onRemoveProfessional: handleRemoveProfessional = () => {}
}) {
  const [showScheduleModal, setShowScheduleModal] = useState (false)
  const [showScheduleDetailsModal, setShowScheduleDetailsModal] = useState (false)
  const [showEditReservationModal, setShowEditReservationModal] = useState (false)
  const [showEditBillModal, setShowEditBillModal] = useState (false)
  const [showEditScheduleDialog, setShowEditScheduleDialog] = useState (false)
  const [showEditReservationDialog, setShowEditReservationDialog] = useState (false)
  const [createScheduleData, setCreateScheduleData] = useState (null)
  const [scheduleData, setScheduleData] = useState (null)
  const [editScheduleData, setEditScheduleData] = useState (null)
  const [reservationData, setReservationData] = useState (null)
  const [billData, setBillData] = useState (null)
  const [errorCode, setErrorCode] = useState (null)
  const [errorMessage, setErrorMessage] = useState (null)
  const selectedDate = DateTime.fromJSDate (date)
  const currentWeek = TimeWindow.getWeekDaysFromDate (selectedDate.toISODate ())
  const previousDayISODate = selectedDate.minus ({days: 1}).toISODate ()
  const nextDayISODate = selectedDate.plus ({days: 1}).toISODate ()
  const token = AuthService.getToken ()
  const businessId = AuthService.getBusinessId ()

  const professionalWithDynamicInterval = useMemo (() => {
    const scheduleBaseInterval = schedules.some (s => (
      s.duration && s.duration % 30 !== 0
    )) ? 15 : 30
    
    return {
      ...professional,
      time_interval: Math.min (professional.time_interval, scheduleBaseInterval)
    }
  }, [professional, schedules])
  
  const sensors = useSensors (useSensor (PointerSensor, {
    activationConstraint: {
      distance: 3
    }
  }))

  const requestConfig = {
    headers: {
      'Authorization': `Bearer ${token}`
    }
  }

  const handleDragEnd = event => {
    const { schedule, type } = event.active.data.current
    const { time, professional } = event.over.data.current

    setEditScheduleData ({...schedule, type, time, professional})

    if (type === 'schedule') toggleEditScheduleDialog ()
    else if (type === 'reservation') toggleEditReservationDialog ()
  }

  const editSchedule = async () => {
    const scheduleValues = {
      date: editScheduleData.date,
      time: editScheduleData.time,
      duration: editScheduleData.duration,
      customerId: editScheduleData.customer.id,
      businessId: editScheduleData.business.id,
      serviceId: editScheduleData.service.id,
      professionalId: editScheduleData.professional.id,
      price: editScheduleData.price
    }

    try {
      const { data: schedule } = await cutileiApi.put (
        `/schedules/${editScheduleData.id}`, scheduleValues, requestConfig
      )

      handleEditSchedule (schedule)
    } catch (error) {
      logApiError (error)
      if (error.response?.data) {
        setErrorCode (error.response.data.error)
        setErrorMessage (getApiErrorMessage (error))
      }
    }
  }

  const editReservation = async () => {
    try {
      const startTime = DateTime.fromFormat (editScheduleData.start_time, 'H:mm')
      const endTime = DateTime.fromFormat (editScheduleData.end_time, 'H:mm')
      const selectedTime = DateTime.fromFormat (editScheduleData.time, 'H:mm')
      const timePeriod = endTime.diff (startTime, 'minutes').toObject ().minutes

      const { data: reservationData } = await cutileiApi.put (`/schedule_reservations/${editScheduleData.id}`, {
        professionalId: editScheduleData.professional.id,
        startTime: editScheduleData.time,
        endTime: selectedTime.plus ({minutes: timePeriod}).toFormat ('H:mm'),
        weekdays: editScheduleData.weekdays
      }, requestConfig)

      handleEditReservation (reservationData)
    } catch (error) {
      setErrorMessage (getApiErrorMessage (error))
      logApiError (error)
    }
  }
  
  const editReservationDay = async () => {
    const reservationValues = {
      professionalId: editScheduleData.professional_id,
      description: editScheduleData.description,
      startDate: editScheduleData.start_date,
      endDate: editScheduleData.end_date,
      startTime: editScheduleData.start_time,
      endTime: editScheduleData.end_time,
      recurrenceType: editScheduleData.recurrence_type,
      recurrenceInterval: editScheduleData.recurrence_interval,
      weekdays: editScheduleData.weekdays
    }

    const startTime = DateTime.fromFormat (reservationValues.startTime, 'H:mm')
    const endTime = DateTime.fromFormat (reservationValues.endTime, 'H:mm')
    const selectedTime = DateTime.fromFormat (editScheduleData.time, 'H:mm')
    const timePeriod = endTime.diff (startTime, 'minutes').toObject ().minutes
    const selectedISODate = selectedDate.toISODate ()

    try {
      await cutileiApi.post (`/businesses/${businessId}/schedule_reservations`, {
        ...reservationValues,
        ...(selectedISODate === reservationValues.startDate
          ? {startDate: nextDayISODate}
          : {endDate: previousDayISODate}
        )
      }, requestConfig)

      if (![reservationValues.startDate, reservationValues.endDate].includes (selectedISODate))
        cutileiApi.post (`/businesses/${businessId}/schedule_reservations`, {
          ...reservationValues,
          startDate: nextDayISODate
        }, requestConfig)

      const { data: reservationData } = await cutileiApi.put (
        `/schedule_reservations/${editScheduleData.id}`, {
          professionalId: editScheduleData.professional.id,
          startDate: selectedISODate,
          endDate: selectedISODate,
          startTime: editScheduleData.time,
          endTime: selectedTime.plus ({minutes: timePeriod}).toFormat ('H:mm'),
          weekdays: editScheduleData.weekdays
        }, requestConfig
      )

      handleEditReservation (reservationData)
    } catch (error) {
      setErrorMessage (getApiErrorMessage (error))
      logApiError (error)
    }
  }

  const professionalTimeWindows = ScheduleGridService.getProfessionalTimeWindows (
    professionalWithDynamicInterval, timeWindows, timeInterval
  )

  const toggleScheduleModal = () => setShowScheduleModal (!showScheduleModal)
  const toggleScheduleDetailsModal = () => setShowScheduleDetailsModal (!showScheduleDetailsModal)
  const toggleEditReservationModal = () => setShowEditReservationModal (!showEditReservationModal)
  const toggleEditBillModal = () => setShowEditBillModal (!showEditBillModal)
  const toggleEditScheduleDialog = () => setShowEditScheduleDialog (!showEditScheduleDialog)
  const toggleEditReservationDialog = () => setShowEditReservationDialog (!showEditReservationDialog)
  
  return (
    <Container>
      <GridHeader numColumns={1}>
        <Professional
          data={professionalWithDynamicInterval}
          onRemove={handleRemoveProfessional}
        />
      </GridHeader>
      <GridHeader
        numColumns={numColumns}
        style={{paddingTop: 2, zIndex: 2, top: 90}}
      >
        {currentWeek.map ((day, index) => (
          <div key={index}>
            <SubTitle>
              {day.setLocale ('pt-BR').toFormat ('cccc')}
            </SubTitle>
            <SubTitle style={{marginBottom: 5}}>
              {day.setLocale ('pt-BR').toFormat ('dd/LLLL')}
            </SubTitle>
          </div>
        ))}
      </GridHeader>
      <div style={{position: 'relative'}}>
        <GridColumn
          numRows={professionalTimeWindows.length}
          timeInterval={professionalWithDynamicInterval.time_interval || timeInterval}
          style={{width: 60}}
        >
          {professionalTimeWindows.map ((time, index) => (
            <TimeMark key={index} label={time}/>
          ))}
        </GridColumn>
        <DndContext onDragEnd={handleDragEnd} sensors={sensors}>
          <ScheduleGrid numRows={professionalTimeWindows.length} numColumns={numColumns}>
            {currentWeek.map ((day, dayIndex) => {
              const daySchedules = schedules.filter (schedule => {
                const startDate = DateTime.fromISO (schedule.start_date)
                const endDate = DateTime.fromISO (schedule.end_date)
                const isReservation = schedule.validated === undefined

                if (!isReservation) return schedule.date === day.toISODate ()
                
                return (startDate <= day && day <= endDate) && (
                  schedule.weekdays.some (d => d.iso_number === day.weekday)
                )
              }) ?? []

              const maxConflicts = ScheduleGridService.getNumConflictingSchedules (
                daySchedules, professionalTimeWindows, professionalWithDynamicInterval.time_interval || timeInterval
              )
              
              return (
                <ProfessionalColumn
                  key={dayIndex}
                  professional={professionalWithDynamicInterval}
                  numRows={professionalTimeWindows.length}
                  timeWindows={professionalTimeWindows}
                  timeInterval={professionalWithDynamicInterval.time_interval || timeInterval}
                  schedules={daySchedules}
                >
                  <GridColumn 
                    numRows={professionalTimeWindows.length}
                    professional={professionalWithDynamicInterval}
                    timeInterval={professionalWithDynamicInterval.time_interval || timeInterval}
                  >
                    {professionalTimeWindows.map ((time, index) => (
                      <HorizontalLine key={index} label={time}/>
                    ))}
                  </GridColumn>
                  {query === '' && (
                    <GridColumn 
                      numRows={professionalTimeWindows.length}
                      professional={professionalWithDynamicInterval}
                      timeInterval={professionalWithDynamicInterval.time_interval || timeInterval}
                    >
                      {professionalTimeWindows.map ((time, timeIndex) => (
                        timeIndex < professionalTimeWindows.length -1 && (
                          <TimeBlock
                            key={timeIndex}
                            id={timeIndex}
                            professional={professionalWithDynamicInterval}
                            timeInterval={professionalWithDynamicInterval.time_interval || timeInterval}
                            numColumns={numColumns}
                            label={time}
                            onClick={() => {
                              setCreateScheduleData ({
                                professional: professionalWithDynamicInterval, date: day, time
                              })
                              toggleScheduleModal ()
                            }}
                          />
                        )
                      ))}
                    </GridColumn>
                  )}
                  {daySchedules.map ((schedule, index) => (
                    <Schedule
                      key={schedule.id}
                      data={schedule}
                      numColumns={numColumns}
                      timeWindows={professionalTimeWindows}
                      timeInterval={professionalWithDynamicInterval.time_interval || timeInterval}
                      schedules={daySchedules}
                      scheduleStatuses={scheduleStatuses}
                      maxConflicts={maxConflicts}
                      position={index + 2}
                      onClick={() => {
                        const isReservation = schedule.validated === undefined
                        if (isReservation) {
                          setReservationData (schedule)
                          toggleEditReservationModal ()
                        } else {
                          setScheduleData (schedule)
                          toggleScheduleDetailsModal ()
                        }
                      }}
                    />
                  ))}
                </ProfessionalColumn>
              )
            })}
          </ScheduleGrid>
        </DndContext>
      </div>
      {createScheduleData && (
        <CreateScheduleModal
          visible={showScheduleModal}
          data={createScheduleData}
          workingDays={workingDays}
          professionals={[professional]}
          onConfirm={handleScheduleCreated}
          onClose={toggleScheduleModal}
        />
      )}
      {scheduleData && (
        <>
          <ScheduleDetailsModal
            visible={showScheduleDetailsModal}
            data={scheduleData}
            schedules={schedules}
            scheduleStatuses={scheduleStatuses}
            workingDays={workingDays}
            onEdit={schedule => {
              setScheduleData (schedule)
              handleEditSchedule (schedule)
              toggleScheduleDetailsModal ()
            }}
            onStatusListUpdated={handleStatusListUpdated}
            onEditStatus={schedules => {
              handleEditSchedulesStatus (schedules)
              toggleScheduleDetailsModal ()
            }}
            onSplit={schedules => {
              handleSplitSchedule (schedules)
              toggleScheduleDetailsModal ()
            }}
            onDelete={handleDeleteSchedule}
            onDeleteAll={handleDeleteAllSchedules}
            onAddSchedule={customer => {
              setCreateScheduleData ({date: selectedDate, customer})
              toggleScheduleDetailsModal ()
              toggleScheduleModal ()
            }}
            onClose={toggleScheduleDetailsModal}
            onOpenBill={bill => {
              setBillData (bill)
              setScheduleData ({...scheduleData, bill_id: bill.id})
              handleEditSchedule ({...scheduleData, bill_id: bill.id})
              toggleScheduleDetailsModal ()
              toggleEditBillModal ()
            }}
            onEditBill={bill => {
              setBillData (bill)
              toggleScheduleDetailsModal ()
              toggleEditBillModal ()
            }}
          />
          {billData && (
            <EditBillModal
              visible={showEditBillModal}
              data={billData}
              onClose={toggleEditBillModal}
              onDeleteSchedule={handleDeleteSchedule}
              onEditScheduleStatus={handleEditSchedulesStatus}
              onCloseBill={() => {
                setScheduleData ({...scheduleData, status: 'paid', validated: true})
              }}
            />
          )}
        </>
      )}
      {reservationData && (
        <EditReservationModal
          visible={showEditReservationModal}
          date={selectedDate}
          data={reservationData}
          timeInterval={timeInterval}
          onConfirm={handleEditReservation}
          onDelete={handleDeleteSchedule}
          onClose={toggleEditReservationModal}
        />
      )}
      {editScheduleData && (() => {
        const { professional } = editScheduleData
        const isOneDayPeriod = editScheduleData.start_date === editScheduleData.end_date

        return (
          <>
            <AlertDialog
              visible={showEditScheduleDialog}
              title='Atenção!'
              message={
                <InfoText>
                  Deseja realmente alterar este agendamento para as
                  <b> {editScheduleData.time}</b> com <b>{professional.nickname}</b>?
                </InfoText>
              }
              confirmText='Sim'
              cancelText='Não'
              onConfirm={editSchedule}
              onClose={toggleEditScheduleDialog}
              containerStyle={{width: 360}}
            />
            <OptionDialog
              visible={showEditReservationDialog}
              title='Atenção!'
              message={
                isOneDayPeriod ? (
                  <InfoText>
                    Deseja realmente alterar esta reserva para as
                    <b> {editScheduleData.time}</b> com <b>{professional.nickname}</b>?
                  </InfoText>
                ) : (
                  <InfoText>
                    Deseja alterar esta reserva para as <b> {editScheduleData.time} </b>
                    com <b>{professional.nickname}</b> em todos os dias de seu período 
                    ou apenas o dia selecionado ({selectedDate.toFormat ('dd/MM/yyyy')})?
                  </InfoText>
                )
              }
              options={isOneDayPeriod ? [
                {label: 'Sim', onClick: editReservation, dangerous: false},
                {label: 'Não', onClick: toggleEditReservationDialog, dangerous: false}
              ] : [
                {label: 'Alterar reserva', onClick: editReservation, dangerous: false},
                {label: 'Alterar dia', onClick: editReservationDay, dangerous: false}
              ]}
              onClose={toggleEditReservationDialog}
              containerStyle={{width: 400}}
              buttonStyle={!isOneDayPeriod && {width: 160}}
            />
          </>
        )
      }) ()}
      <AlertDialog
        visible={errorMessage !== null}
        title='Erro'
        message={<>
          {errorMessage}
          {errorCode === 'unavailable_day' && errorMessage?.includes ('profissional') && (
            <InfoText>
              {'Se deseja agendar neste horário, altere a '}
              <LinkText
                to={{ pathname: `/business/professionals/${editScheduleData?.professional.id}/edit` }}
                state={{ configOption: 'agenda' }}
              >
                agenda do profissional
              </LinkText>
            </InfoText>
          )}
        </>}
        confirmText='Ok'
        onConfirm={() => setErrorMessage (null)}
        onClose={() => setErrorMessage (null)}
      />
    </Container>
  )
}
